/**
 * Feature Hooks Configuration
 *
 * Feature hooks are "virtual hooks" that represent feature toggles in the agent configuration.
 * They are displayed in the Hooks Pipeline UI alongside real hooks, but they control
 * whether certain tabs are shown/hidden based on their enabled state.
 *
 * IMPORTANT: There are TWO types of enablement:
 * 1. Extension Enable (扩展启用): Shows the configuration tab in UI
 *    - Controlled by UI state (expandedTabs)
 *    - Allows user to configure the feature without enabling it
 *
 * 2. Feature Enable (正式启用): Actually enables the feature in the agent
 *    - Controlled by the actual config field (e.g., memory.enabled)
 *    - Set inside the feature's configuration tab
 *
 * These hooks are treated as SYSTEM stage hooks that don't modify prompts,
 * but instead control feature activation.
 */

export type FeatureHookId =
  | 'memory'
  | 'storage'
  | 'knowledge'
  | 'team'
  | 'classifier'
  | 'astrology_plus'
  | 'datetime'
  | 'emotion'
  | 'debug';

/** Mapping from feature ID to linked tab ID */
export type FeatureTabMapping = {
  memory: 'memory';
  knowledge: 'knowledge';
  classifier: 'classifier';
  team: 'team';
  astrology_plus: 'astrology';
  emotion: 'emotion';
};

export interface FeatureHook {
  id: FeatureHookId;
  displayName: string;
  description: string;
  icon: string; // Lucide icon name
  /** Path to read the actual enabled state (for display only) */
  configPath: string;
  /** Tab ID to show when extension is expanded */
  linkedTab?: string;
  order: number; // Display order in the feature list
  category: 'core' | 'extension' | 'debug';
  /** If true, toggling this feature directly modifies configPath (no separate expansion) */
  directToggle?: boolean;
}

/**
 * Feature hooks configuration
 * Order determines display sequence in the Features section
 *
 * Features with linkedTab: toggling shows/hides the tab (扩展启用)
 * Features with directToggle: toggling directly enables/disables the feature (正式启用)
 */
export const FEATURE_HOOKS: FeatureHook[] = [
  // Core features (direct toggle - no configuration needed)
  {
    id: 'storage',
    displayName: '会话存储',
    description: '存储聊天会话历史记录',
    icon: 'Database',
    configPath: 'storage_enabled',
    order: 10,
    category: 'core',
    directToggle: true, // Simple toggle, no configuration tab
  },
  {
    id: 'datetime',
    displayName: '当前时间',
    description: '在系统指令中包含当前日期和时间',
    icon: 'Settings',
    configPath: 'add_datetime_to_instructions',
    order: 15,
    category: 'core',
    directToggle: true, // Simple toggle, no configuration tab
  },
  // Extension features (expansion - have dedicated configuration tabs)
  {
    id: 'memory',
    displayName: '记忆系统',
    description: '长期记忆存储和检索',
    icon: 'Brain',
    configPath: 'memory.enabled',
    linkedTab: 'memory',
    order: 20,
    category: 'extension',
    // No directToggle: expanding shows tab, memory.enabled inside tab controls actual enablement
  },
  {
    id: 'knowledge',
    displayName: '知识库',
    description: '向量知识库用于上下文感知响应',
    icon: 'BookOpen',
    configPath: 'knowledge_enabled',
    linkedTab: 'knowledge',
    order: 30,
    category: 'extension',
    // Expandable: shows tab for configuration
  },
  {
    id: 'classifier',
    displayName: '响应分类器',
    description: '根据消息复杂度分类响应类型',
    icon: 'Filter',
    configPath: 'hooks.classifier.enabled',
    linkedTab: 'classifier',
    order: 40,
    category: 'extension',
    // No directToggle: expanding shows tab, hooks.classifier.enabled inside tab controls actual enablement
  },
  {
    id: 'team',
    displayName: '团队模式',
    description: '多Agent协作',
    icon: 'Users',
    configPath: 'agent_mode',
    linkedTab: 'team',
    order: 50,
    category: 'extension',
    // No directToggle: expanding shows tab, agent_mode inside tab controls actual mode
  },
  {
    id: 'astrology_plus',
    displayName: '占星增强',
    description: '出生图、本命盘计算和行运预测',
    icon: 'Star',
    configPath: 'hooks.astrology_plus.enabled',
    linkedTab: 'astrology',
    order: 60,
    category: 'extension',
    // Expandable: shows tab for astrology_plus configuration (birth chart + transit)
  },
  {
    id: 'emotion',
    displayName: '情绪感知',
    description: '分析用户情绪和意图，增强Agent理解能力',
    icon: 'Heart',
    configPath: 'hooks.emotion_enhance.enabled',
    linkedTab: 'emotion',
    order: 70,
    category: 'extension',
    // Expandable: shows tab for emotion_enhance configuration
  },
  // Debug features (direct toggle)
  {
    id: 'debug',
    displayName: '调试模式',
    description: '在响应中显示详细执行信息',
    icon: 'Bug',
    configPath: 'debug_mode',
    order: 100,
    category: 'debug',
    directToggle: true, // Simple toggle, no configuration needed
  },
];

/**
 * Get feature hooks grouped by category
 */
export function getFeatureHooksByCategory(): Record<string, FeatureHook[]> {
  const groups: Record<string, FeatureHook[]> = {};

  for (const hook of FEATURE_HOOKS) {
    if (!groups[hook.category]) {
      groups[hook.category] = [];
    }
    groups[hook.category].push(hook);
  }

  // Sort each group by order
  for (const category of Object.keys(groups)) {
    groups[category].sort((a, b) => a.order - b.order);
  }

  return groups;
}

/**
 * Get a feature hook by ID
 */
export function getFeatureHook(id: FeatureHookId): FeatureHook | undefined {
  return FEATURE_HOOKS.find(h => h.id === id);
}

/**
 * Check if a feature is enabled based on config value
 */
export function isFeatureEnabled(id: FeatureHookId, value: any): boolean {
  switch (id) {
    case 'team':
      // agent_mode is 'single' or 'team'
      return value === 'team';
    default:
      return Boolean(value);
  }
}

/**
 * Get the value to set when enabling/disabling a feature (for directToggle features)
 */
export function getFeatureToggleValue(id: FeatureHookId, enabled: boolean): any {
  switch (id) {
    case 'team':
      // agent_mode uses 'single' or 'team'
      return enabled ? 'team' : 'single';
    default:
      return enabled;
  }
}

/**
 * Get all expandable features (features with linkedTab and no directToggle)
 * These features control tab visibility through UI state, not config
 */
export function getExpandableFeatures(): FeatureHook[] {
  return FEATURE_HOOKS.filter(f => f.linkedTab && !f.directToggle);
}

/**
 * Get the linked tab ID for a feature
 */
export function getLinkedTab(id: FeatureHookId): string | undefined {
  const feature = getFeatureHook(id);
  return feature?.linkedTab;
}

export default FEATURE_HOOKS;
